import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Wallet, QrCode, ArrowDownRight, Clock, CheckCircle, 
  Home, Menu, X, Globe, User, LogOut, Settings, Banknote, Loader2,
  ScanLine, AlertCircle, ShoppingBag, Receipt, HelpCircle, Download, Printer, BarChart3
} from 'lucide-react';
import QRCode from 'react-qr-code';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import MerchantOnboardingGuide from '../../components/MerchantOnboardingGuide';
import MerchantAnalytics from '../../components/MerchantAnalytics';
import MerchantPurchaseRequests from '../../components/MerchantPurchaseRequests';

import { API_URL } from '../../utils/apiConfig';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.4, ease: 'easeOut' },
};

export default function MerchantDashboard() {
  const [searchParams] = useSearchParams();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [qrToken, setQrToken] = useState(null);
  const [balance, setBalance] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [todayEarnings, setTodayEarnings] = useState(0);
  const [activeTab, setActiveTab] = useState('redemptions');

  // Redemption state
  const [pendingRedemptions, setPendingRedemptions] = useState([]);
  const [redeemDialogOpen, setRedeemDialogOpen] = useState(false);
  const [tokenInput, setTokenInput] = useState('');
  const [validatedAuth, setValidatedAuth] = useState(null);
  const [redemptionAmount, setRedemptionAmount] = useState('');
  const [validating, setValidating] = useState(false);
  const [redeeming, setRedeeming] = useState(false);
  const [redemptionHistory, setRedemptionHistory] = useState([]);
  
  // New purchase request state
  const [purchaseRequestId, setPurchaseRequestId] = useState(null);
  const [waitingForConfirmation, setWaitingForConfirmation] = useState(false);
  const [purchaseRequestStatus, setPurchaseRequestStatus] = useState(null);
  
  // Onboarding guide state
  const [showOnboarding, setShowOnboarding] = useState(false);
  
  // Analytics state
  const [showAnalytics, setShowAnalytics] = useState(false);
  
  // QR Poster modal state
  const [showQRPosterModal, setShowQRPosterModal] = useState(false);

  const { language, toggleLanguage, t } = useLanguage();
  const { user, wallet, token, logout } = useAuth();

  useEffect(() => {
    if (token) {
      fetchTransactions();
      fetchQRCode();
      fetchBalance();
      fetchPayouts();
      fetchPendingRedemptions();
      fetchRedemptionHistory();
      
      // Check if first time merchant (show onboarding)
      const hasSeenOnboarding = localStorage.getItem('merchant_onboarding_complete');
      if (!hasSeenOnboarding) {
        setShowOnboarding(true);
      }
    }
  }, [token]);
  
  const handleOnboardingComplete = () => {
    localStorage.setItem('merchant_onboarding_complete', 'true');
    setShowOnboarding(false);
  };
  
  // Download QR Poster (opens modal with enlarged QR code)
  const openQRPosterModal = () => {
    setShowQRPosterModal(true);
  };
  
  // Download QR Poster as PDF
  const downloadQRPosterPDF = async () => {
    try {
      const response = await fetch(`${API_URL}/api/merchant/qr-poster?language=${language}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to download');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `PeCash_QR_Poster.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      
      toast.success(language === 'es' ? 'Poster descargado' : 'Poster downloaded');
    } catch (error) {
      toast.error(language === 'es' ? 'Error al descargar' : 'Failed to download');
    }
  };

  // Check for pending redeem code from QR scan
  useEffect(() => {
    const redeem = searchParams.get('redeem');
    const pendingCode = sessionStorage.getItem('pendingRedeemCode');
    
    if (redeem === 'true' && pendingCode) {
      // Clear the stored code
      sessionStorage.removeItem('pendingRedeemCode');
      // Open redeem dialog with the code pre-filled
      setTokenInput(pendingCode);
      setRedeemDialogOpen(true);
      // Auto-validate the code
      setTimeout(() => {
        handleValidateTokenWithCode(pendingCode);
      }, 500);
    }
  }, [searchParams]);

  const handleValidateTokenWithCode = async (code) => {
    if (!code.trim()) return;

    setValidating(true);
    try {
      const response = await fetch(`${API_URL}/api/authorizations/redeem/validate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code: code.trim() }),
      });

      const data = await response.json();

      if (response.ok && data.ok) {
        setValidatedAuth({
          authorization_id: data.authorization_id,
          reference_id: data.reference_id,
          recipient_name: data.recipient_first_name,
          dop_authorized_amount: data.amount_dop,
          dop_remaining_amount: data.dop_remaining,
          dop_redeemed_amount: data.dop_redeemed,
          usd_amount: data.amount_usd,
          expires_at: data.expires_at,
          status: data.status,
        });
        toast.success(language === 'es' ? 'Código válido' : 'Code validated');
      } else {
        // Show specific error from backend
        const errorMsg = data.detail || (language === 'es' ? 'Código inválido' : 'Invalid code');
        toast.error(errorMsg);
        setValidatedAuth(null);
      }
    } catch (error) {
      console.error('Validation error:', error);
      toast.error(language === 'es' ? 'Error de conexión' : 'Connection error');
    } finally {
      setValidating(false);
    }
  };

  const fetchPendingRedemptions = async () => {
    try {
      const response = await fetch(`${API_URL}/api/authorizations/merchant/pending`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setPendingRedemptions(data.pending_redemptions || []);
      }
    } catch (error) {
      console.error('Failed to fetch pending redemptions:', error);
    }
  };

  const fetchRedemptionHistory = async () => {
    try {
      const response = await fetch(`${API_URL}/api/authorizations/merchant/history`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setRedemptionHistory(data.redemptions || []);
      }
    } catch (error) {
      console.error('Failed to fetch redemption history:', error);
    }
  };

  const handleValidateToken = async () => {
    if (!tokenInput.trim()) {
      toast.error(language === 'es' ? 'Ingrese un código' : 'Enter a code');
      return;
    }

    setValidating(true);
    try {
      const response = await fetch(`${API_URL}/api/authorizations/redeem/validate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code: tokenInput.trim() }),
      });

      const data = await response.json();

      if (response.ok && data.ok) {
        // Map new response format to expected format
        setValidatedAuth({
          authorization_id: data.authorization_id,
          reference_id: data.reference_id,
          recipient_name: data.recipient_first_name,
          dop_authorized_amount: data.amount_dop,
          dop_remaining_amount: data.dop_remaining,
          dop_redeemed_amount: data.dop_redeemed,
          usd_amount: data.amount_usd,
          expires_at: data.expires_at,
          status: data.status,
        });
        toast.success(language === 'es' ? 'Código válido' : 'Code validated');
      } else {
        toast.error(data.detail || 'Invalid code');
        setValidatedAuth(null);
      }
    } catch (error) {
      toast.error(language === 'es' ? 'Error al validar' : 'Validation failed');
    } finally {
      setValidating(false);
    }
  };

  // Handle complete delivery (full redemption)
  const handleCompleteDelivery = async () => {
    if (!validatedAuth?.authorization_id) {
      toast.error('No authorization to complete');
      return;
    }

    setRedeeming(true);
    try {
      const response = await fetch(`${API_URL}/api/authorizations/redeem/complete`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          authorization_id: validatedAuth.authorization_id,
        }),
      });

      const data = await response.json();

      if (response.ok && data.ok) {
        toast.success(data.message || 'Delivery completed! Funds credited.');
        setRedeemDialogOpen(false);
        resetRedeemForm();
        fetchPendingRedemptions();
        fetchRedemptionHistory();
        fetchBalance();
      } else {
        toast.error(data.detail || 'Completion failed');
      }
    } catch (error) {
      toast.error(language === 'es' ? 'Error al completar' : 'Completion failed');
    } finally {
      setRedeeming(false);
    }
  };

  const handleRedeemPurchase = async () => {
    const amount = parseFloat(redemptionAmount);
    if (!amount || amount <= 0) {
      toast.error(language === 'es' ? 'Ingrese un monto válido' : 'Enter a valid amount');
      return;
    }

    if (amount > validatedAuth.dop_remaining_amount) {
      toast.error(language === 'es' 
        ? `Monto excede el balance. Máximo: RD$${validatedAuth.dop_remaining_amount.toLocaleString()}` 
        : `Amount exceeds balance. Max: RD$${validatedAuth.dop_remaining_amount.toLocaleString()}`);
      return;
    }

    setRedeeming(true);
    try {
      const response = await fetch(`${API_URL}/api/authorizations/redeem`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token_value: tokenInput.trim(),
          redemption_amount_dop: amount,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(data.message || 'Redemption successful');
        setRedeemDialogOpen(false);
        resetRedeemForm();
        fetchPendingRedemptions();
        fetchRedemptionHistory();
        fetchBalance();
      } else {
        toast.error(data.detail || 'Redemption failed');
      }
    } catch (error) {
      toast.error(language === 'es' ? 'Error al canjear' : 'Redemption failed');
    } finally {
      setRedeeming(false);
    }
  };

  const resetRedeemForm = () => {
    setTokenInput('');
    setValidatedAuth(null);
    setRedemptionAmount('');
    setPurchaseRequestId(null);
    setWaitingForConfirmation(false);
    setPurchaseRequestStatus(null);
  };

  // New: Create purchase request and wait for beneficiary confirmation
  const handleCreatePurchaseRequest = async () => {
    const amount = parseFloat(redemptionAmount);
    if (!amount || amount <= 0) {
      toast.error(language === 'es' ? 'Ingrese un monto válido' : 'Enter a valid amount');
      return;
    }

    if (amount > validatedAuth.dop_remaining_amount) {
      toast.error(language === 'es' 
        ? `Monto excede el balance. Máximo: RD$${validatedAuth.dop_remaining_amount.toLocaleString()}` 
        : `Amount exceeds balance. Max: RD$${validatedAuth.dop_remaining_amount.toLocaleString()}`);
      return;
    }

    setRedeeming(true);
    try {
      const response = await fetch(`${API_URL}/api/merchant-redeem/create-purchase-request`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          authorization_id: validatedAuth.authorization_id,
          purchase_amount_dop: amount,
        }),
      });

      const data = await response.json();

      if (response.ok && data.ok) {
        setPurchaseRequestId(data.purchase_request.id);
        setWaitingForConfirmation(true);
        toast.success(language === 'es' 
          ? 'Esperando confirmación del beneficiario...' 
          : 'Waiting for beneficiary confirmation...');
        
        // Start polling for confirmation
        pollPurchaseRequestStatus(data.purchase_request.id);
      } else {
        toast.error(data.detail || 'Failed to create purchase request');
      }
    } catch (error) {
      toast.error(language === 'es' ? 'Error al crear solicitud' : 'Request creation failed');
    } finally {
      setRedeeming(false);
    }
  };

  // Poll for purchase request status
  const pollPurchaseRequestStatus = async (requestId) => {
    let attempts = 0;
    const maxAttempts = 100; // ~5 minutes with 3 second intervals
    
    const poll = async () => {
      if (attempts >= maxAttempts) {
        setWaitingForConfirmation(false);
        toast.error(language === 'es' 
          ? 'Tiempo de espera agotado. El beneficiario no confirmó.' 
          : 'Timeout. Beneficiary did not confirm.');
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/merchant-redeem/purchase-request/${requestId}/status`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });

        const data = await response.json();

        if (response.ok) {
          setPurchaseRequestStatus(data.status);

          if (data.status === 'completed') {
            setWaitingForConfirmation(false);
            toast.success(language === 'es' 
              ? '¡Compra confirmada! Fondos acreditados.' 
              : 'Purchase confirmed! Funds credited.');
            fetchPendingRedemptions();
            fetchRedemptionHistory();
            fetchBalance();
            setTimeout(() => {
              setRedeemDialogOpen(false);
              resetRedeemForm();
            }, 2000);
            return;
          } else if (data.status === 'cancelled') {
            setWaitingForConfirmation(false);
            toast.warning(language === 'es' 
              ? 'El beneficiario canceló la compra.' 
              : 'Beneficiary cancelled the purchase.');
            return;
          } else if (data.status === 'expired') {
            setWaitingForConfirmation(false);
            toast.error(language === 'es' 
              ? 'La solicitud expiró. Intente de nuevo.' 
              : 'Request expired. Please try again.');
            return;
          }
          
          // Still pending, continue polling
          attempts++;
          setTimeout(poll, 3000);
        }
      } catch (error) {
        console.error('Polling error:', error);
        attempts++;
        setTimeout(poll, 3000);
      }
    };

    poll();
  };

  const fetchBalance = async () => {
    try {
      const response = await fetch(`${API_URL}/api/merchants/balance`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setBalance(data);
      }
    } catch (error) {
      console.error('Failed to fetch balance:', error);
    }
  };

  const fetchPayouts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/merchants/payouts`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setPayouts(data.payouts || []);
      }
    } catch (error) {
      console.error('Failed to fetch payouts:', error);
    }
  };

  const fetchTransactions = async () => {
    try {
      const response = await fetch(`${API_URL}/api/merchants/transactions`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      if (response.ok) {
        const data = await response.json();
        setTransactions(data.transactions || []);
        
        // Calculate today's earnings
        const today = new Date().toDateString();
        const todayTx = data.transactions?.filter(tx => 
          new Date(tx.created_at).toDateString() === today && tx.status === 'completed'
        ) || [];
        const earnings = todayTx.reduce((sum, tx) => sum + tx.amount, 0);
        setTodayEarnings(earnings);
      }
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchQRCode = async () => {
    try {
      const response = await fetch(`${API_URL}/api/merchants/qr-code`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      if (response.ok) {
        const data = await response.json();
        setQrToken(data.qr_token);
      }
    } catch (error) {
      console.error('Failed to fetch QR code:', error);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-emerald-600" />;
      case 'pending':
        return <Clock className="w-4 h-4 text-amber-600" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-emerald-50 text-emerald-700';
      case 'pending':
        return 'bg-amber-50 text-amber-700';
      default:
        return 'bg-slate-50 text-slate-700';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="merchant-dashboard">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden" 
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 h-full w-64 bg-white border-r border-slate-200 z-50 transform transition-transform duration-300 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="p-6">
          <Link to="/" className="flex items-center gap-2 mb-8">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-600 to-emerald-700 flex items-center justify-center">
              <span className="text-white font-bold text-lg">P</span>
            </div>
            <span className="font-bold text-xl text-slate-900">PeCash</span>
          </Link>

          <nav className="space-y-2">
            <Link
              to="/app/merchant"
              className="flex items-center gap-3 px-4 py-3 rounded-xl bg-blue-50 text-blue-700 font-medium"
              data-testid="sidebar-dashboard"
            >
              <Wallet className="w-5 h-5" />
              {t('dashboard.merchant.title')}
            </Link>
            <button
              onClick={() => { setRedeemDialogOpen(true); resetRedeemForm(); }}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-slate-600 hover:bg-emerald-50 hover:text-emerald-700 transition-colors"
              data-testid="sidebar-redeem"
            >
              <ScanLine className="w-5 h-5" />
              {t('authorization.redeemPurchase')}
              {pendingRedemptions.length > 0 && (
                <span className="ml-auto bg-emerald-100 text-emerald-700 text-xs px-2 py-0.5 rounded-full">
                  {pendingRedemptions.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('purchases')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                activeTab === 'purchases' 
                  ? 'bg-purple-50 text-purple-700 font-medium' 
                  : 'text-slate-600 hover:bg-purple-50 hover:text-purple-700'
              }`}
              data-testid="sidebar-purchase-requests"
            >
              <ShoppingBag className="w-5 h-5" />
              {language === 'es' ? 'Solicitudes de Compra' : 'Purchase Requests'}
            </button>
            <button
              onClick={() => setShowAnalytics(true)}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-slate-600 hover:bg-blue-50 hover:text-blue-700 transition-colors"
              data-testid="sidebar-analytics"
            >
              <BarChart3 className="w-5 h-5" />
              {language === 'es' ? 'Análisis de Ventas' : 'Sales Analytics'}
            </button>
            <button
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-slate-600 hover:bg-slate-50 transition-colors"
              data-testid="sidebar-qr"
            >
              <QrCode className="w-5 h-5" />
              {t('dashboard.merchant.yourQRCode')}
            </button>
            <button
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-slate-600 hover:bg-slate-50 transition-colors"
              data-testid="sidebar-settlements"
            >
              <Banknote className="w-5 h-5" />
              {t('dashboard.merchant.settlements')}
            </button>
            <button
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-slate-600 hover:bg-slate-50 transition-colors"
              data-testid="sidebar-settings"
            >
              <Settings className="w-5 h-5" />
              {t('dashboard.merchant.profile')}
            </button>
            <Link
              to="/"
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-600 hover:bg-slate-50 transition-colors"
              data-testid="sidebar-home"
            >
              <Home className="w-5 h-5" />
              {t('nav.home')}
            </Link>
          </nav>
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-slate-200">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
              <User className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="font-medium text-slate-900 text-sm">{user?.name || 'Merchant'}</p>
              <p className="text-xs text-slate-500">{user?.business_name || user?.email}</p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            className="w-full justify-start text-slate-600" 
            onClick={logout}
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            {t('common.logOut')}
          </Button>
          
          {/* Download QR Poster */}
          <Button 
            variant="outline" 
            className="w-full justify-start text-blue-600 border-blue-200 hover:bg-blue-50 mt-2" 
            onClick={openQRPosterModal}
            data-testid="download-qr-poster-btn"
          >
            <Printer className="w-4 h-4 mr-2" />
            {language === 'es' ? 'Descargar Poster QR' : 'Download QR Poster'}
          </Button>
          
          {/* Help / Onboarding Guide Button */}
          <Button 
            variant="outline" 
            className="w-full justify-start text-emerald-600 border-emerald-200 hover:bg-emerald-50 mt-2" 
            onClick={() => setShowOnboarding(true)}
            data-testid="show-onboarding-btn"
          >
            <HelpCircle className="w-4 h-4 mr-2" />
            {language === 'es' ? 'Guía de Inicio' : 'Getting Started Guide'}
          </Button>
        </div>
      </aside>
      
      {/* Merchant Onboarding Guide */}
      <MerchantOnboardingGuide 
        isOpen={showOnboarding}
        onClose={() => setShowOnboarding(false)}
        onComplete={handleOnboardingComplete}
      />

      {/* Main Content */}
      <div className="lg:pl-64">
        {/* Top Bar */}
        <header className="sticky top-0 bg-white/80 backdrop-blur-md border-b border-slate-200 z-30">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="lg:hidden p-2 rounded-lg hover:bg-slate-100 transition-colors"
                data-testid="mobile-sidebar-toggle"
              >
                {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
              <h1 className="text-xl font-bold text-slate-900">{t('dashboard.merchant.title')}</h1>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={toggleLanguage}
                data-testid="dashboard-language-toggle"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
              >
                <Globe className="w-4 h-4" />
                <span>{language.toUpperCase()}</span>
              </button>
            </div>
          </div>
        </header>

        {/* Dashboard Content */}
        {activeTab !== 'purchases' && (
        <main className="p-6">
          <motion.div
            initial="initial"
            animate="animate"
            variants={{ animate: { transition: { staggerChildren: 0.1 } } }}
          >
            {/* Welcome */}
            <motion.div variants={fadeInUp} className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900">
                {t('dashboard.welcome')}, {user?.name?.split(' ')[0] || 'Merchant'}
              </h2>
              <p className="text-slate-600">{user?.business_name}</p>
            </motion.div>

            {/* Stats Grid */}
            <motion.div variants={fadeInUp} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {/* Settlement Amount */}
              <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm" data-testid="settlement-amount-card">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm font-medium text-slate-500">{t('dashboard.merchant.payoutAmount')}</span>
                  <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                    <Wallet className="w-5 h-5 text-emerald-600" />
                  </div>
                </div>
                <p className="text-3xl font-bold text-slate-900">${(wallet?.balance || 0).toFixed(2)}</p>
              </div>

              {/* Today's Earnings */}
              <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm" data-testid="today-earnings-card">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm font-medium text-slate-500">{t('dashboard.merchant.todayEarnings')}</span>
                  <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                    <ArrowDownRight className="w-5 h-5 text-blue-600" />
                  </div>
                </div>
                <p className="text-3xl font-bold text-slate-900">${todayEarnings.toFixed(2)}</p>
              </div>

              {/* QR Code Card */}
              <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm md:col-span-2 lg:col-span-1" data-testid="qr-code-card">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm font-medium text-slate-500">{t('dashboard.merchant.yourQRCode')}</span>
                  <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                    <QrCode className="w-5 h-5 text-purple-600" />
                  </div>
                </div>
                <div className="flex items-center justify-center p-4 bg-white border-2 border-dashed border-slate-200 rounded-xl">
                  <div className="text-center">
                    {qrToken ? (
                      <div className="mb-3" data-testid="merchant-qr-code">
                        <QRCode 
                          value={`${window.location.origin}/pay/${qrToken}`}
                          size={128}
                          level="M"
                          bgColor="#ffffff"
                          fgColor="#000000"
                        />
                      </div>
                    ) : (
                      <div className="w-32 h-32 mx-auto bg-slate-100 rounded-lg flex items-center justify-center mb-3">
                        {loading ? (
                          <Loader2 className="w-8 h-8 text-slate-400 animate-spin" />
                        ) : (
                          <QrCode className="w-20 h-20 text-slate-400" />
                        )}
                      </div>
                    )}
                    <p className="text-xs text-slate-500">{t('dashboard.merchant.qrInstructions')}</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Settlement Notice */}
            <motion.div variants={fadeInUp} className="mb-8">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3" data-testid="settlement-notice">
                <Banknote className="w-5 h-5 text-amber-600 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-800">{t('dashboard.merchant.settlements')}</p>
                  <p className="text-sm text-amber-700">{t('dashboard.merchant.settlementNote')}</p>
                </div>
              </div>
            </motion.div>

            {/* Balance & Payout Summary */}
            {balance && (
              <motion.div variants={fadeInUp} className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm" data-testid="payout-balance-card">
                  <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <Banknote className="w-5 h-5 text-emerald-600" />
                    {t('dashboard.merchant.payoutBalance')}
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600">{t('dashboard.merchant.availableForPayout')}</span>
                      <span className="text-xl font-bold text-emerald-600">${balance.available_for_payout?.toFixed(2) || '0.00'}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600">{t('dashboard.merchant.pendingPayout')}</span>
                      <span className="font-medium text-amber-600">${balance.pending_payout?.toFixed(2) || '0.00'}</span>
                    </div>
                    <div className="flex justify-between items-center pt-2 border-t border-slate-100">
                      <span className="text-slate-600">{t('dashboard.merchant.totalPaidOut')}</span>
                      <span className="font-medium text-slate-900">${balance.total_paid_out?.toFixed(2) || '0.00'}</span>
                    </div>
                  </div>
                </div>
                
                {balance.last_payout && (
                  <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm" data-testid="last-payout-card">
                    <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-emerald-600" />
                      {t('dashboard.merchant.lastPayout')}
                    </h3>
                    <div className="text-center py-4">
                      <p className="text-3xl font-bold text-emerald-600">${balance.last_payout.amount?.toFixed(2)}</p>
                      <p className="text-sm text-slate-500 mt-2">
                        {t('dashboard.status.completed')} {new Date(balance.last_payout.completed_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                )}
              </motion.div>
            )}

            {/* Payout History */}
            {payouts.length > 0 && (
              <motion.div variants={fadeInUp} className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden mb-8" data-testid="payouts-history">
                <div className="px-6 py-4 border-b border-slate-100">
                  <h3 className="font-semibold text-slate-900">{t('dashboard.merchant.payoutHistory')}</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">{t('tables.payoutId')}</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">{t('common.amount')}</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">{t('common.status')}</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">{t('common.date')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {payouts.slice(0, 5).map((payout) => (
                        <tr key={payout.payout_id} className="hover:bg-slate-50">
                          <td className="px-6 py-4 font-mono text-xs text-slate-900">{payout.payout_id}</td>
                          <td className="px-6 py-4 font-semibold text-emerald-600">${(payout.net_amount || payout.total_amount)?.toFixed(2)}</td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium capitalize ${getStatusColor(payout.status)}`}>
                              {getStatusIcon(payout.status)}
                              {t(`dashboard.status.${payout.status}`) || payout.status?.replace(/_/g, ' ')}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-slate-500">
                            {formatDate(payout.created_at)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </motion.div>
            )}

            {/* Recent Transactions */}
            <motion.div variants={fadeInUp} className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-testid="transactions-table">
              <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                <h3 className="font-semibold text-slate-900">{t('dashboard.recentTransactions')}</h3>
                <Button variant="ghost" className="text-blue-700 text-sm">
                  {t('dashboard.viewAll')}
                </Button>
              </div>
              {loading ? (
                <div className="p-8 text-center">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto text-slate-400" />
                </div>
              ) : transactions.length === 0 ? (
                <div className="p-8 text-center text-slate-500">
                  <Wallet className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>{t('tables.noTransactionsYet')}</p>
                  <p className="text-sm">{t('dashboard.merchant.shareQRNote')}</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">{t('common.sender')}</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">{t('common.amount')}</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">{t('common.status')}</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">{t('common.date')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {transactions.map((tx) => (
                        <tr key={tx.id} className="hover:bg-slate-50 transition-colors" data-testid={`transaction-${tx.id}`}>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                                <ArrowDownRight className="w-5 h-5 text-emerald-600" />
                              </div>
                              <span className="font-medium text-slate-900">
                                {tx.description || t('dashboard.merchant.paymentReceived')}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="font-semibold text-emerald-600">+${tx.amount?.toFixed(2)}</span>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(tx.status)}`}>
                              {getStatusIcon(tx.status)}
                              {t(`dashboard.status.${tx.status}`) || tx.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-slate-500">
                            {formatDate(tx.created_at)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </motion.div>
          </motion.div>
        </main>
        )}

        {/* Purchase Requests Tab */}
        {activeTab === 'purchases' && (
          <main className="p-6">
            <MerchantPurchaseRequests />
          </main>
        )}
      </div>

      {/* Redeem Purchase Dialog */}
      <Dialog open={redeemDialogOpen} onOpenChange={(open) => { 
        setRedeemDialogOpen(open); 
        if (!open) resetRedeemForm(); 
      }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ScanLine className="w-5 h-5 text-emerald-600" />
              {t('authorization.redeemPurchase')}
            </DialogTitle>
            <DialogDescription>
              {language === 'es' 
                ? 'Ingrese el código del cliente para canjear productos.'
                : 'Enter the customer code to redeem goods.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Token Input */}
            {!validatedAuth && (
              <>
                <div>
                  <Label htmlFor="tokenInput">{t('authorization.scanQR')}</Label>
                  <Input
                    id="tokenInput"
                    type="text"
                    placeholder={language === 'es' ? 'Código de 8 dígitos...' : '8-digit code...'}
                    value={tokenInput}
                    onChange={(e) => setTokenInput(e.target.value)}
                    className="font-mono text-lg tracking-widest"
                    data-testid="token-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">{t('authorization.enterCode')}</p>
                </div>
                <Button
                  onClick={handleValidateToken}
                  disabled={validating || !tokenInput.trim()}
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                  data-testid="validate-token-btn"
                >
                  {validating ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                    <>
                      <ScanLine className="w-4 h-4 mr-2" />
                      {t('authorization.validateCode')}
                    </>
                  )}
                </Button>
              </>
            )}

            {/* Validated Authorization Details */}
            {validatedAuth && (
              <>
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                    <span className="font-semibold text-emerald-800">
                      {language === 'es' ? 'Código válido' : 'Valid Code'}
                    </span>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-emerald-700">{t('authorization.referenceId')}</span>
                      <span className="font-mono font-medium">{validatedAuth.reference_id}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-emerald-700">{t('authorization.recipientName')}</span>
                      <span className="font-medium">{validatedAuth.recipient_name}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-50 rounded-xl p-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">{t('authorization.authorizedDOP')}</span>
                    <span className="font-medium">RD${validatedAuth.dop_authorized_amount?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">{t('authorization.redeemedDOP')}</span>
                    <span className="font-medium text-blue-600">RD${validatedAuth.dop_redeemed_amount?.toLocaleString()}</span>
                  </div>
                  <div className="border-t border-slate-200 pt-2">
                    <div className="flex justify-between font-semibold">
                      <span>{t('authorization.remainingDOP')}</span>
                      <span className="text-emerald-600">RD${validatedAuth.dop_remaining_amount?.toLocaleString()}</span>
                    </div>
                  </div>
                </div>

                <div>
                  <Label htmlFor="redemptionAmount">{t('authorization.redemptionAmount')}</Label>
                  <Input
                    id="redemptionAmount"
                    type="number"
                    step="0.01"
                    min="0.01"
                    max={validatedAuth.dop_remaining_amount}
                    placeholder="0.00"
                    value={redemptionAmount}
                    onChange={(e) => setRedemptionAmount(e.target.value)}
                    className="text-lg"
                    data-testid="redemption-amount-input"
                  />
                  <div className="flex gap-2 mt-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setRedemptionAmount(Math.min(500, validatedAuth.dop_remaining_amount).toString())}
                    >
                      RD$500
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setRedemptionAmount(Math.min(1000, validatedAuth.dop_remaining_amount).toString())}
                    >
                      RD$1,000
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setRedemptionAmount(validatedAuth.dop_remaining_amount.toString())}
                    >
                      {t('common.all')} (RD${validatedAuth.dop_remaining_amount?.toLocaleString()})
                    </Button>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={resetRedeemForm}
                    className="flex-1"
                    disabled={waitingForConfirmation}
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button
                    onClick={handleCreatePurchaseRequest}
                    disabled={redeeming || waitingForConfirmation || !redemptionAmount || parseFloat(redemptionAmount) <= 0}
                    className="flex-1 bg-blue-600 hover:bg-blue-700"
                    data-testid="partial-redemption-btn"
                  >
                    {redeeming || waitingForConfirmation ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <ShoppingBag className="w-4 h-4 mr-2" />
                        {language === 'es' ? 'Solicitar Compra' : 'Request Purchase'}
                      </>
                    )}
                  </Button>
                </div>

                {/* Waiting for Beneficiary Confirmation */}
                {waitingForConfirmation && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-center">
                    <Loader2 className="w-8 h-8 animate-spin text-amber-600 mx-auto mb-2" />
                    <p className="font-semibold text-amber-800">
                      {language === 'es' 
                        ? 'Esperando confirmación del beneficiario...' 
                        : 'Waiting for beneficiary confirmation...'}
                    </p>
                    <p className="text-sm text-amber-700 mt-1">
                      {language === 'es' 
                        ? 'El beneficiario debe confirmar la compra en su teléfono'
                        : 'The beneficiary must confirm the purchase on their phone'}
                    </p>
                    <p className="text-xs text-amber-600 mt-2">
                      {language === 'es' 
                        ? `Monto: RD$${parseFloat(redemptionAmount).toLocaleString()}`
                        : `Amount: RD$${parseFloat(redemptionAmount).toLocaleString()}`}
                    </p>
                  </div>
                )}

                {/* Legacy Complete Delivery Button - for full redemption without confirmation */}
                {!waitingForConfirmation && (
                  <div className="border-t pt-4 mt-4">
                    <Button
                      onClick={handleCompleteDelivery}
                      disabled={redeeming}
                      className="w-full bg-emerald-600 hover:bg-emerald-700 h-12"
                      data-testid="complete-delivery-btn"
                    >
                      {redeeming ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                        <>
                          <CheckCircle className="w-5 h-5 mr-2" />
                          {language === 'es' 
                            ? `Completar Entrega - RD$${validatedAuth.dop_remaining_amount?.toLocaleString()}`
                            : `Complete Delivery - RD$${validatedAuth.dop_remaining_amount?.toLocaleString()}`}
                        </>
                      )}
                    </Button>
                    <p className="text-xs text-slate-500 text-center mt-2">
                      {language === 'es' 
                        ? 'Marca como entregado y acredita el monto completo (sin confirmación del beneficiario)'
                        : 'Mark as delivered and credit full amount (without beneficiary confirmation)'}
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Merchant Analytics Modal */}
      <MerchantAnalytics 
        token={token} 
        isOpen={showAnalytics} 
        onClose={() => setShowAnalytics(false)} 
      />
      
      {/* QR Poster Modal */}
      <Dialog open={showQRPosterModal} onOpenChange={setShowQRPosterModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="w-5 h-5 text-purple-600" />
              {language === 'es' ? 'Tu Código QR de Tienda' : 'Your Store QR Code'}
            </DialogTitle>
            <DialogDescription>
              {language === 'es' 
                ? 'Muestra este código QR a los clientes para que escaneen y paguen sus compras autorizadas.'
                : 'Display this QR code for customers to scan and pay for their authorized purchases.'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex flex-col items-center py-6">
            {qrToken ? (
              <>
                <div id="printable-qr-poster" className="bg-white p-6 rounded-2xl border-2 border-slate-200 shadow-lg" data-testid="enlarged-qr-code">
                  <QRCode 
                    value={`${window.location.origin}/pay/${qrToken}`}
                    size={256}
                    level="H"
                    bgColor="#ffffff"
                    fgColor="#000000"
                  />
                </div>
                
                <p className="text-sm text-slate-600 mt-4 text-center max-w-xs">
                  {language === 'es' 
                    ? 'Los clientes escanean este código para pagar con su autorización de compra PeCash.'
                    : 'Customers scan this code to pay with their PeCash purchase authorization.'}
                </p>
                
                <div className="flex flex-wrap gap-3 mt-6 justify-center">
                  <Button 
                    onClick={() => {
                      // Create print-ready content
                      const businessName = user?.name || 'Colmado';
                      const qrUrl = `${window.location.origin}/pay/${qrToken}`;
                      const printWindow = window.open('', '_blank');
                      printWindow.document.write(`
                        <!DOCTYPE html>
                        <html>
                        <head>
                          <title>PeCash QR Poster - ${businessName}</title>
                          <style>
                            @page { size: A4; margin: 0; }
                            * { margin: 0; padding: 0; box-sizing: border-box; }
                            body { 
                              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                              display: flex;
                              flex-direction: column;
                              align-items: center;
                              justify-content: center;
                              min-height: 100vh;
                              background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
                              padding: 40px;
                            }
                            .poster {
                              background: white;
                              border-radius: 24px;
                              padding: 48px;
                              box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
                              text-align: center;
                              max-width: 500px;
                              width: 100%;
                            }
                            .logo {
                              font-size: 32px;
                              font-weight: bold;
                              color: #7c3aed;
                              margin-bottom: 8px;
                            }
                            .business-name {
                              font-size: 24px;
                              font-weight: 600;
                              color: #1e293b;
                              margin-bottom: 24px;
                            }
                            .qr-container {
                              background: white;
                              padding: 24px;
                              border: 3px solid #e2e8f0;
                              border-radius: 16px;
                              display: inline-block;
                              margin: 24px 0;
                            }
                            .qr-container img { display: block; }
                            .instruction {
                              font-size: 18px;
                              color: #475569;
                              margin-bottom: 16px;
                              line-height: 1.5;
                            }
                            .instruction-es {
                              font-size: 16px;
                              color: #64748b;
                              font-style: italic;
                            }
                            .footer {
                              margin-top: 32px;
                              padding-top: 24px;
                              border-top: 2px solid #e2e8f0;
                              color: #94a3b8;
                              font-size: 12px;
                            }
                            .pecash-badge {
                              display: inline-flex;
                              align-items: center;
                              gap: 8px;
                              background: linear-gradient(135deg, #7c3aed 0%, #6366f1 100%);
                              color: white;
                              padding: 8px 16px;
                              border-radius: 24px;
                              font-weight: 600;
                              font-size: 14px;
                              margin-top: 16px;
                            }
                            @media print {
                              body { background: white; padding: 20px; }
                              .poster { box-shadow: none; border: 2px solid #e2e8f0; }
                            }
                          </style>
                        </head>
                        <body>
                          <div class="poster">
                            <div class="logo">PeCash</div>
                            <div class="business-name">${businessName}</div>
                            
                            <div class="qr-container">
                              <img src="https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(qrUrl)}" alt="QR Code" width="300" height="300" />
                            </div>
                            
                            <p class="instruction">Scan to pay with your PeCash authorization</p>
                            <p class="instruction-es">Escanea para pagar con tu autorización PeCash</p>
                            
                            <div class="pecash-badge">
                              ✓ Accepted Here
                            </div>
                            
                            <div class="footer">
                              <p>PeCashGlobal • Secure Purchase Authorizations</p>
                              <p style="margin-top: 8px;">www.pecashglobal.com</p>
                            </div>
                          </div>
                        </body>
                        </html>
                      `);
                      printWindow.document.close();
                      printWindow.onload = () => {
                        setTimeout(() => {
                          printWindow.print();
                        }, 500);
                      };
                      toast.success(language === 'es' ? 'Abriendo vista de impresión...' : 'Opening print preview...');
                    }}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="print-qr-btn"
                  >
                    <Printer className="w-4 h-4 mr-2" />
                    {language === 'es' ? 'Imprimir' : 'Print'}
                  </Button>
                  <Button 
                    onClick={downloadQRPosterPDF}
                    className="bg-purple-600 hover:bg-purple-700"
                    data-testid="download-pdf-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {language === 'es' ? 'Descargar PDF' : 'Download PDF'}
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => {
                      const qrUrl = `${window.location.origin}/pay/${qrToken}`;
                      navigator.clipboard.writeText(qrUrl);
                      toast.success(language === 'es' ? 'Enlace copiado' : 'Link copied');
                    }}
                    data-testid="copy-qr-link-btn"
                  >
                    {language === 'es' ? 'Copiar Enlace' : 'Copy Link'}
                  </Button>
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center py-8">
                <div className="w-48 h-48 bg-slate-100 rounded-2xl flex items-center justify-center mb-4">
                  <QrCode className="w-24 h-24 text-slate-300" />
                </div>
                <p className="text-slate-500">
                  {language === 'es' ? 'No hay código QR disponible' : 'No QR code available'}
                </p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
